import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Upload,
  Loader2,
  X,
  Ruler,
  AlertCircle,
  Trash2,
  Plus,
  Crop,
  Scaling,
  Download,
  Archive,
  CheckCircle2,
  Crosshair,
  MoveVertical,
} from 'lucide-react';

const API_BASE = 'https://api.glowytics.com';

// Saydam (alpha) eşiği — bunun altındaki pikseller "boş" sayılır.
const ALPHA_THRESHOLD = 10;

// Şişe üst/alt tespiti için (güçlü vision modeli — mekânsal akıl yürütme için pro).
// Not: Tek referansımız şişenin BAŞLANGIÇ (üst) ve BİTİŞ (alt) noktası.
// AI sadece bir başlangıç önerisi verir; kullanıcı çizgileri elle düzeltebilir.
const DETECT_MODEL = 'gemini-2.5-pro';
const DETECT_PROMPT = `Görselde dik duran bir cam parfüm şişesi var; tepesinde PARLAK METAL/ALTIN bir KAPAK bulunur. Etrafında organik dekor var (çiçek, yaprak, meyve, karpuz, gül, bal, baharat, tarçın, nar vb.) ve bu dekor cam gövdeye BİTİŞİK olabilir.

ÖNEMLİ: Cam gövdenin sol/sağ kenarını ARAMA (dekor bitişik, güvenilmez). Bunun yerine şişenin tam ortasındaki METAL KAPAĞA odaklan — kapak dar, parlak, simetrik ve dekordan kolayca ayrılır.

Şu 4 değeri ver (hepsi 0-1000 normalize, sol-üst köşe 0,0):
- cap_top  = metal kapağın EN TEPE Y noktası (kapağın üstüne taşan çiçek/dal varsa SAYMA).
- base_bottom = cam şişenin DÜZ TABANININ en alt Y'si (altına yığılan baharat/meyveyi SAYMA).
- cap_left  = SADECE metal kapağın sol kenarı X (kapağın yanındaki çiçeği/dekoru SAYMA).
- cap_right = SADECE metal kapağın sağ kenarı X (kapağın yanındaki çiçeği/dekoru SAYMA).

Kapak şişenin dikey simetri ekseni üzerindedir; cap_left ve cap_right SADECE sert metal kapağı sarsın, çok dar bir aralık olsun.

Yalnızca şu JSON'u döndür, başka hiçbir açıklama yazma:
{"cap_top":<sayı>,"base_bottom":<sayı>,"cap_left":<sayı>,"cap_right":<sayı>}`;

const CHECKER_BG = 'bg-[conic-gradient(#f3f4f6_90deg,#fff_90deg_180deg,#f3f4f6_180deg_270deg,#fff_270deg)] bg-[length:16px_16px]';

// Çıktı boyut presetleri (genişlik × yükseklik).
const OUTPUT_PRESETS = [
  { label: '1500 × 1750', w: 1500, h: 1750 },
  { label: '1080 × 1350', w: 1080, h: 1350 },
  { label: '1080 × 1080', w: 1080, h: 1080 },
  { label: '2000 × 2000', w: 2000, h: 2000 },
];

function uid() {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('Görsel okunamadı'));
    i.src = url;
  });
}

function canvasToBlob(canvas) {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
}

// Saydam olmayan piksellerin sınır kutusu (tüm içerik = şişe + dekor).
function findContentBox(img) {
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, w, h);
  let data;
  try {
    data = ctx.getImageData(0, 0, w, h).data;
  } catch (_) {
    return { x: 0, y: 0, width: w, height: h, isFullCanvas: true };
  }
  let minX = w, minY = h, maxX = -1, maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const a = data[(y * w + x) * 4 + 3];
      if (a > ALPHA_THRESHOLD) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return { x: 0, y: 0, width: w, height: h, isFullCanvas: true };
  return { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1, isFullCanvas: false };
}

async function analyzeFile(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const box = findContentBox(img);
    return {
      previewUrl: url,
      box,
      naturalWidth: img.naturalWidth || img.width,
      naturalHeight: img.naturalHeight || img.height,
      size: file.size,
    };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}

// Başlangıç kılavuzları (0-100 yüzde): üst/alt + sol/sağ kenar.
// Üst/alt = içerik kutusunun dikey sınırı (kabaca doğru). Sol/sağ = merkez %50 çevresinde
// simetrik başlar (%40/%60); dekora kayan içerik-kenarı KULLANILMAZ. AI sonra kapak
// kenarlarından gerçek değerleri koyar, kullanıcı elle düzeltebilir.
function initialGuides(box, W, H) {
  if (!box || box.isFullCanvas || W <= 0 || H <= 0) {
    return { topPct: 10, bottomPct: 90, leftPct: 40, rightPct: 60 };
  }
  return {
    topPct: (box.y / H) * 100,
    bottomPct: ((box.y + box.height) / H) * 100,
    leftPct: 40,
    rightPct: 60,
  };
}

// AI sonucunu, kullanıcının ELLE kilitlediği eksenlere dokunmadan birleştir.
// vManual=true ise üst/alt korunur; hManual=true ise yatay merkez korunur.
function mergeAiGuides(it, g) {
  const patch = { aiDone: true, detectError: '' };
  if (!it.vManual) { patch.topPct = g.topPct; patch.bottomPct = g.bottomPct; }
  if (!it.hManual && g.leftPct != null && g.rightPct != null) { patch.leftPct = g.leftPct; patch.rightPct = g.rightPct; }
  return { ...it, ...patch };
}

// Gemini'nin döndürdüğü JSON'dan üst/alt ve KAPAK merkezini (yatay) ayıkla.
function parseBottleGuides(text) {
  if (!text) return null;
  const t = String(text).replace(/```json/gi, '').replace(/```/g, '').trim();
  let obj = null;
  try { obj = JSON.parse(t); } catch (_) {
    const m = t.match(/\{[\s\S]*\}/);
    if (m) { try { obj = JSON.parse(m[0]); } catch (__) { /* ignore */ } }
  }
  if (Array.isArray(obj)) obj = obj[0];
  if (!obj || typeof obj !== 'object') return null;
  const num = (v) => (isFinite(Number(v)) ? Number(v) : null);
  const clamp = (v) => Math.max(0, Math.min(1000, v));

  // Tercih edilen kapak-merkezli şema
  let top = num(obj.cap_top), bottom = num(obj.base_bottom);
  let capL = num(obj.cap_left), capR = num(obj.cap_right);

  // Eski kutu şeması (ymin/ymax/xmin/xmax) — geriye dönük uyumluluk
  if (top == null && obj.ymin != null) top = num(obj.ymin);
  if (bottom == null && obj.ymax != null) bottom = num(obj.ymax);
  if (capL == null && obj.xmin != null) capL = num(obj.xmin);
  if (capR == null && obj.xmax != null) capR = num(obj.xmax);

  if (top == null || bottom == null) return null;
  top = clamp(top); bottom = clamp(bottom);
  if (bottom < top) [top, bottom] = [bottom, top];
  if (bottom - top < 5) return null;

  let leftPct = null, rightPct = null;
  if (capL != null && capR != null) {
    let l = clamp(capL), r = clamp(capR);
    if (r < l) [l, r] = [r, l];
    leftPct = l / 10; rightPct = r / 10;
  }
  return { topPct: top / 10, bottomPct: bottom / 10, leftPct, rightPct };
}

// Şişe üst/alt çizgisini Gemini ile tespit et — 0-100 yüzde döner.
async function detectBottleGuides(item, brandId) {
  const img = await loadImage(item.previewUrl);

  // Tespit için küçült (yüzde normalize olduğu için çözünürlükten bağımsız)
  const maxSide = 1152;
  const s = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
  const cw = Math.max(1, Math.round(img.naturalWidth * s));
  const ch = Math.max(1, Math.round(img.naturalHeight * s));
  const c = document.createElement('canvas');
  c.width = cw; c.height = ch;
  const cx = c.getContext('2d');
  cx.fillStyle = '#ffffff'; // şeffaf PNG'yi beyaz zemine bas (jpeg'de siyah olmasın)
  cx.fillRect(0, 0, cw, ch);
  cx.drawImage(img, 0, 0, cw, ch);
  const dataUrl = c.toDataURL('image/jpeg', 0.9);
  const b64 = dataUrl.split(',')[1] || '';

  const resp = await fetch(`${API_BASE}/api/ai/gemini/chat`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId },
    body: JSON.stringify({
      model: DETECT_MODEL,
      temperature: 0,
      // pro thinking'i kapatamaz (min bütçe); düşünme + tek satır JSON için bütçeyi bol tut,
      // yoksa düşünme tokenları sınırı yer ve cevap boş/truncated döner (finishReason=MAX_TOKENS).
      maxOutputTokens: 4096,
      messages: [{ role: 'user', content: DETECT_PROMPT, images: [{ mimeType: 'image/jpeg', data: b64 }] }],
    }),
  });
  const txt = await resp.text();
  let json;
  try { json = JSON.parse(txt); } catch { json = { message: txt }; }
  if (!resp.ok) throw new Error(json?.error || resp.statusText || 'Tespit isteği başarısız');

  const g = parseBottleGuides(json?.message || '');
  if (!g) throw new Error(`Şişe tespit edilemedi: "${String(json?.message || '').slice(0, 60)}"`);

  // Üst/alt'ı alpha ile KESİNLEŞTİR: merkez şeritte seed'den yukarı/aşağı bitişik opak koşu.
  // AI'ın iki gürültülü noktası yerine gerçek şişe silüetini ölçer → aynı flakonlar
  // çıktıda AYNI BOYDA olur. Kopuk dekoru dışlar; kapağa/tabana yapışık dekorda AI'ya düşer.
  const centerX = (g.leftPct != null && g.rightPct != null) ? (g.leftPct + g.rightPct) / 2 : 50;
  const refined = refineEdgesByAlpha(img, centerX, g.topPct, g.bottomPct);
  if (refined) { g.topPct = refined.topPct; g.bottomPct = refined.bottomPct; }
  return g;
}

// Şişe üst/alt'ını alpha ile kesinleştir (deterministik, AI'dan bağımsız boy ölçümü).
// Merkez ±%2'lik dar şeritte, şişe gövdesinden (seed) yukarı ve aşağı bitişik opak koşu izlenir;
// ufak şeffaf boşluklara (maxgap) izin verilir ama büyük boşlukta durur → kopuk dekor dışlanır.
function refineEdgesByAlpha(img, centerXPct, topPct, bottomPct) {
  try {
    const W0 = img.naturalWidth || img.width;
    const H0 = img.naturalHeight || img.height;
    if (!W0 || !H0) return null;
    const s = Math.min(1, 1200 / Math.max(W0, H0));
    const W = Math.max(1, Math.round(W0 * s));
    const H = Math.max(1, Math.round(H0 * s));
    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    const ctx = c.getContext('2d');
    ctx.drawImage(img, 0, 0, W, H);
    const data = ctx.getImageData(0, 0, W, H).data;
    const TH = 12;
    const cx = Math.round((centerXPct / 100) * W);
    const d = Math.max(2, Math.round(0.02 * W));
    const x0 = Math.max(0, cx - d);
    const x1 = Math.min(W - 1, cx + d);
    const rowHas = (y) => {
      const base = y * W;
      for (let x = x0; x <= x1; x++) { if (data[(base + x) * 4 + 3] > TH) return true; }
      return false;
    };
    let seed = Math.round(((topPct + bottomPct) / 2 / 100) * H);
    seed = Math.max(0, Math.min(H - 1, seed));
    if (!rowHas(seed)) return null; // seed boşsa (merkez yanlış) güvenme
    const maxgap = Math.max(2, Math.round(0.0015 * H));
    let topY = seed, gap = 0;
    for (let y = seed; y >= 0; y--) { if (rowHas(y)) { topY = y; gap = 0; } else if (++gap > maxgap) break; }
    let botY = seed; gap = 0;
    for (let y = seed; y < H; y++) { if (rowHas(y)) { botY = y; gap = 0; } else if (++gap > maxgap) break; }
    if (botY - topY < H * 0.2) return null;
    return { topPct: (topY / H) * 100, bottomPct: (botY / H) * 100 };
  } catch (_) {
    return null;
  }
}

// Tek görseli eşitle.
// Şişenin üst çizgisi hedef üst-Y'ye, alt çizgisi hedef alt-Y'ye oturur.
// Tüm görsel aynı dönüşümle çizilir; dekor şişeyle birlikte kayar, taşan kısım kırpılır.
async function equalizeOne(item, opts) {
  const { sizes, topTargetPct, bottomTargetPct, anchorXPct, bgColor } = opts;
  const img = await loadImage(item.previewUrl);
  const srcW = img.naturalWidth || img.width;
  const srcH = img.naturalHeight || img.height;

  // Kaynak şişe üst/alt + yatay merkez (sol/sağ kenarların ortası) — tüm çıktı boyutları için ortak.
  const bottleTopY = (item.topPct / 100) * srcH;
  const bottleBottomY = (item.bottomPct / 100) * srcH;
  const bottleH = Math.max(1, bottleBottomY - bottleTopY);
  const bottleCenterX = ((item.leftPct + item.rightPct) / 2 / 100) * srcW;

  // Her hedef boyut için ayrı kanvas üret (ör. 1500×1750 + 1500×1500).
  const results = [];
  for (const { w: outW, h: outH } of sizes) {
    const canvas = document.createElement('canvas');
    canvas.width = outW; canvas.height = outH;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    if (bgColor) { ctx.fillStyle = bgColor; ctx.fillRect(0, 0, outW, outH); }
    else ctx.clearRect(0, 0, outW, outH);

    // Hedef üst/alt (px) — her görselde sabit
    const targetTopY = (topTargetPct / 100) * outH;
    const targetBottomY = (bottomTargetPct / 100) * outH;
    const targetH = targetBottomY - targetTopY;

    // Şişe merkezi her görselde kanvasın AYNI X noktasına (anchorXPct) oturur → yatay kayma olmaz.
    const scale = targetH / bottleH;
    const targetCenterX = (anchorXPct / 100) * outW;
    const offsetX = targetCenterX - bottleCenterX * scale;
    const offsetY = targetTopY - bottleTopY * scale;

    ctx.drawImage(img, 0, 0, srcW, srcH, offsetX, offsetY, srcW * scale, srcH * scale);

    const blob = await canvasToBlob(canvas);
    results.push({
      blob,
      url: URL.createObjectURL(blob),
      outW,
      outH,
      bottleHpx: Math.round(targetH),
      label: `${outW}×${outH}`,
    });
  }
  return results;
}

// --- Sürüklenebilir kılavuzlar: üst / alt çizgisi + yatay merkez çizgisi ---
function GuideOverlay({ item, disabled, onChange }) {
  const ref = useRef(null);
  const dragRef = useRef(null);

  const clamp = (v) => Math.max(0, Math.min(100, v));

  const start = (which) => (e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = which;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* noop */ }
  };
  const move = (e) => {
    if (!dragRef.current || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const px = clamp(((e.clientX - r.left) / r.width) * 100);
    const py = clamp(((e.clientY - r.top) / r.height) * 100);
    if (dragRef.current === 'top') onChange({ topPct: Math.min(py, item.bottomPct - 1), vManual: true });
    else if (dragRef.current === 'bottom') onChange({ bottomPct: Math.max(py, item.topPct + 1), vManual: true });
    else if (dragRef.current === 'left') onChange({ leftPct: Math.min(px, item.rightPct - 1), hManual: true });
    else if (dragRef.current === 'right') onChange({ rightPct: Math.max(px, item.leftPct + 1), hManual: true });
    else {
      // MERKEZ: SOL ve SAĞ'ı birlikte kaydır, kapak genişliğini koru → şişe eksenini elle tam ortala.
      const half = (item.rightPct - item.leftPct) / 2;
      const c = Math.max(half, Math.min(100 - half, px));
      onChange({ leftPct: c - half, rightPct: c + half, hManual: true });
    }
  };
  const end = () => { dragRef.current = null; };

  // Çıktıda kanvas merkezine (anchorX) oturacak yatay eksen = SOL/SAĞ ortası.
  const centerPct = (item.leftPct + item.rightPct) / 2;

  return (
    <div
      ref={ref}
      className="relative w-full select-none touch-none"
      style={{ aspectRatio: `${item.canvasW || 1}/${item.canvasH || 1}` }}
    >
      <img src={item.previewUrl} alt={item.name} className="absolute inset-0 w-full h-full object-contain pointer-events-none" />

      {/* Sol kenar */}
      <div onPointerDown={start('left')} onPointerMove={move} onPointerUp={end} onPointerCancel={end}
        className={`absolute top-0 bottom-0 -translate-x-1/2 w-5 flex justify-center touch-none ${disabled ? '' : 'cursor-col-resize'}`}
        style={{ left: `${item.leftPct}%` }}>
        <div className="h-full w-0.5 bg-sky-500" />
        <span className="absolute top-1 left-1 text-[9px] font-bold text-white bg-sky-500 px-1 rounded">SOL</span>
      </div>

      {/* Sağ kenar */}
      <div onPointerDown={start('right')} onPointerMove={move} onPointerUp={end} onPointerCancel={end}
        className={`absolute top-0 bottom-0 -translate-x-1/2 w-5 flex justify-center touch-none ${disabled ? '' : 'cursor-col-resize'}`}
        style={{ left: `${item.rightPct}%` }}>
        <div className="h-full w-0.5 bg-violet-500" />
        <span className="absolute bottom-1 right-1 text-[9px] font-bold text-white bg-violet-500 px-1 rounded">SAĞ</span>
      </div>

      {/* Yatay merkez — çıktıda kanvasın ortasına oturacak eksen. Sürükle: SOL/SAĞ birlikte kayar. */}
      <div onPointerDown={start('center')} onPointerMove={move} onPointerUp={end} onPointerCancel={end}
        className={`absolute top-0 bottom-0 -translate-x-1/2 w-3 flex justify-center touch-none ${disabled ? '' : 'cursor-col-resize'}`}
        style={{ left: `${centerPct}%` }}>
        <div className="h-full" style={{ borderLeft: '2px dashed #f59e0b' }} />
        <span className="absolute top-1/2 -translate-y-1/2 left-1 text-[9px] font-bold text-white bg-amber-500 px-1 rounded whitespace-nowrap">MERKEZ</span>
      </div>

      {/* Üst çizgi */}
      <div onPointerDown={start('top')} onPointerMove={move} onPointerUp={end} onPointerCancel={end}
        className={`absolute left-0 right-0 -translate-y-1/2 h-4 flex items-center touch-none ${disabled ? '' : 'cursor-row-resize'}`}
        style={{ top: `${item.topPct}%` }}>
        <div className="w-full h-0.5 bg-emerald-500" />
        <span className="absolute left-1 -top-0.5 -translate-y-full text-[9px] font-bold text-white bg-emerald-500 px-1 rounded">ÜST</span>
      </div>

      {/* Alt çizgi */}
      <div onPointerDown={start('bottom')} onPointerMove={move} onPointerUp={end} onPointerCancel={end}
        className={`absolute left-0 right-0 -translate-y-1/2 h-4 flex items-center touch-none ${disabled ? '' : 'cursor-row-resize'}`}
        style={{ top: `${item.bottomPct}%` }}>
        <div className="w-full h-0.5 bg-rose-500" />
        <span className="absolute left-1 top-1 text-[9px] font-bold text-white bg-rose-500 px-1 rounded">ALT</span>
      </div>
    </div>
  );
}

export default function ImageEqualize() {
  const [items, setItems] = useState([]);
  const [globalError, setGlobalError] = useState('');
  const [busy, setBusy] = useState(false);
  const [equalizing, setEqualizing] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [zipping, setZipping] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, label: '' });

  // Ayarlar
  const [autoDetect, setAutoDetect] = useState(true);
  const [outW, setOutW] = useState(1500);
  const [outH, setOutH] = useState(1750);
  const [topTargetPct, setTopTargetPct] = useState(15);
  const [bottomTargetPct, setBottomTargetPct] = useState(85);
  const [anchorXPct, setAnchorXPct] = useState(50); // şişe merkezinin oturacağı yatay nokta
  const [bgEnabled, setBgEnabled] = useState(true);
  const [bgColor, setBgColor] = useState('#F4F4F6');
  const [alsoSquare, setAlsoSquare] = useState(true); // ana boyut yanında kare (1:1) çıktı da üret

  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  const brandId = useMemo(() => {
    try {
      return (
        localStorage.getItem('currentBrandId') ||
        localStorage.getItem('selectedBrandId') ||
        sessionStorage.getItem('currentBrandId') ||
        sessionStorage.getItem('selectedBrandId') ||
        ''
      );
    } catch (_) {
      return '';
    }
  }, []);

  // En güncel items'a senkron erişim (eşitleme, AI tespitinden sonra taze state okur).
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const updateItem = useCallback((id, patch) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const addFiles = useCallback(async (fileList) => {
    setGlobalError('');
    const all = Array.from(fileList || []);
    const valid = all.filter((f) => String(f.type || '').startsWith('image/'));
    const skipped = all.length - valid.length;
    if (!valid.length) { setGlobalError('Lütfen görsel dosyası seç (PNG önerilir).'); return; }
    if (skipped > 0) setGlobalError(`${skipped} dosya görsel olmadığı için atlandı.`);

    setBusy(true);
    const built = [];
    for (const f of valid) {
      try {
        const a = await analyzeFile(f);
        const g = initialGuides(a.box, a.naturalWidth, a.naturalHeight);
        built.push({
          id: uid(), name: f.name, size: f.size,
          previewUrl: a.previewUrl, canvasW: a.naturalWidth, canvasH: a.naturalHeight,
          box: a.box, results: null, error: '', detectError: '',
          topPct: g.topPct, bottomPct: g.bottomPct, leftPct: g.leftPct, rightPct: g.rightPct,
          vManual: false, hManual: false, aiDone: false,
        });
      } catch (e) {
        built.push({
          id: uid(), name: f.name, size: f.size,
          previewUrl: null, canvasW: 0, canvasH: 0,
          box: null, results: null, error: e?.message || 'Analiz edilemedi.',
          topPct: 10, bottomPct: 90, leftPct: 40, rightPct: 60,
          vManual: false, hManual: false, aiDone: false,
        });
      }
    }
    setItems((prev) => [...prev, ...built]);
    setBusy(false);
  }, []);

  const onFileInputChange = (e) => {
    const fs = e.target.files;
    if (fs && fs.length) addFiles(fs);
    e.target.value = '';
  };
  const onDragEnter = (e) => { e.preventDefault(); e.stopPropagation(); dragCounter.current += 1; setIsDragging(true); };
  const onDragLeave = (e) => {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) { dragCounter.current = 0; setIsDragging(false); }
  };
  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    dragCounter.current = 0; setIsDragging(false);
    const fs = e.dataTransfer?.files;
    if (fs && fs.length) addFiles(fs);
  };

  const revokeResult = (it) => { (it?.results || []).forEach((r) => { if (r?.url) URL.revokeObjectURL(r.url); }); };

  const removeItem = useCallback((id) => {
    setItems((prev) => {
      const it = prev.find((x) => x.id === id);
      if (it?.previewUrl) URL.revokeObjectURL(it.previewUrl);
      revokeResult(it);
      return prev.filter((x) => x.id !== id);
    });
  }, []);

  const clearAll = useCallback(() => {
    if (busy || equalizing || detecting) return;
    setItems((prev) => {
      prev.forEach((it) => { if (it.previewUrl) URL.revokeObjectURL(it.previewUrl); revokeResult(it); });
      return [];
    });
    setGlobalError('');
  }, [busy, equalizing, detecting]);

  const stats = useMemo(() => {
    const ok = items.filter((it) => it.box && !it.error);
    if (!ok.length) return null;
    const aiCount = items.filter((it) => it.aiDone).length;
    const manualCount = items.filter((it) => it.vManual || it.hManual).length;
    return { count: ok.length, aiCount, manualCount };
  }, [items]);

  // Eş zamanlı (concurrency sınırlı) işleyici
  const runPool = useCallback(async (targets, worker, label) => {
    const concurrency = 3;
    let idx = 0;
    let done = 0;
    setProgress({ done: 0, total: targets.length, label });
    const runOne = async (t) => {
      await worker(t);
      done += 1;
      setProgress({ done, total: targets.length, label });
    };
    const workers = Array.from({ length: Math.min(concurrency, targets.length) }, async () => {
      while (idx < targets.length) {
        const my = idx++;
        await runOne(targets[my]);
      }
    });
    await Promise.all(workers);
  }, []);

  // AI tespiti — elle kilitlenen ekseni EZMEZ (eksen bazlı). Tüm geçerli görseller hedef:
  // üst/alt'ı elle ayarlamış olsan bile AI yatay merkezi (centerX) doldurur.
  const runDetect = useCallback(async () => {
    if (detecting || equalizing) return;
    const targets = items.filter((it) => it.box && !it.error && !(it.vManual && it.hManual));
    if (!targets.length) return;
    setGlobalError('');
    setDetecting(true);
    let failed = 0;
    await runPool(targets, async (t) => {
      try {
        const g = await detectBottleGuides(t, brandId);
        setItems((prev) => prev.map((it) => (it.id === t.id ? mergeAiGuides(it, g) : it)));
      } catch (e) {
        failed += 1;
        setItems((prev) => prev.map((it) => it.id === t.id ? { ...it, detectError: e?.message || 'Tespit edilemedi.' } : it));
      }
    }, 'Şişe tespit ediliyor');
    setDetecting(false);
    if (failed) setGlobalError(`${failed}/${targets.length} görselde AI şişeyi bulamadı — o kartlarda yeşil (ÜST) ve kırmızı (ALT) çizgileri elle sürükle.`);
  }, [detecting, equalizing, items, brandId, runPool]);

  const runEqualize = useCallback(async () => {
    if (equalizing || detecting) return;
    const targets = items.filter((it) => it.box && !it.error);
    if (!targets.length) { setGlobalError('Eşitlenecek geçerli görsel yok.'); return; }
    if (!(outW > 0 && outH > 0)) { setGlobalError('Geçerli bir çıktı boyutu gir.'); return; }

    setGlobalError('');

    // AI otomatik: AI hiç çalışmamış ve en az bir ekseni elle kilitli olmayan görselleri tespit et.
    // (Üst/alt'ı elle ayarladıysan bile yatay merkez AI'dan dolar.)
    if (autoDetect) {
      const pending = targets.filter((t) => !t.aiDone && !(t.vManual && t.hManual));
      if (pending.length) {
        setDetecting(true);
        await runPool(pending, async (t) => {
          try {
            const g = await detectBottleGuides(t, brandId);
            setItems((prev) => prev.map((it) => (it.id === t.id ? mergeAiGuides(it, g) : it)));
          } catch (e) {
            setItems((prev) => prev.map((it) => it.id === t.id ? { ...it, detectError: e?.message || 'Tespit edilemedi.' } : it));
          }
        }, 'Şişe tespit ediliyor');
        setDetecting(false);
      }
    }

    setEqualizing(true);
    setItems((prev) => prev.map((it) => { revokeResult(it); return { ...it, results: null }; }));

    // En güncel state'i al (AI/manuel çizgiler dahil)
    const work = itemsRef.current.filter((it) => it.box && !it.error);

    // Üretilecek boyutlar: ana boyut + (istenirse) aynı genişlikte kare (ör. 1500×1750 + 1500×1500).
    const sizes = [{ w: outW, h: outH }];
    if (alsoSquare && outW !== outH) sizes.push({ w: outW, h: outW });

    const opts = { sizes, topTargetPct, bottomTargetPct, anchorXPct, bgColor: bgEnabled ? bgColor : null };
    await runPool(work, async (w) => {
      try {
        const results = await equalizeOne(w, opts);
        setItems((prev) => prev.map((it) => it.id === w.id ? { ...it, results, error: '' } : it));
      } catch (e) {
        setItems((prev) => prev.map((it) => it.id === w.id ? { ...it, error: e?.message || 'Eşitlenemedi.' } : it));
      }
    }, 'Eşitleniyor');
    setEqualizing(false);
  }, [equalizing, detecting, items, autoDetect, outW, outH, topTargetPct, bottomTargetPct, anchorXPct, bgEnabled, bgColor, alsoSquare, brandId, runPool]);

  const downloadResult = useCallback((it, r) => {
    if (!r?.url) return;
    const a = document.createElement('a');
    a.href = r.url;
    const base = (it.name || 'image').replace(/\.[a-z0-9]+$/i, '');
    a.download = `${base}_${r.outW}x${r.outH}.png`;
    a.click();
  }, []);

  const downloadAllZip = useCallback(async () => {
    const done = items.filter((it) => it.results?.length);
    if (!done.length) return;
    setZipping(true);
    try {
      const mod = await import('jszip');
      const JSZip = mod.default || mod;
      const zip = new JSZip();
      const used = new Set(); // klasör+dosya adı bazında benzersizlik
      let fileCount = 0;
      for (const it of done) {
        const base = (it.name || 'image').replace(/\.[a-z0-9]+$/i, '');
        for (const r of it.results) {
          const folder = `${r.outW}x${r.outH}`; // her boyut kendi klasörüne
          let fileName = `${folder}/${base}.png`;
          let n = 1;
          while (used.has(fileName)) fileName = `${folder}/${base}_${n++}.png`;
          used.add(fileName);
          zip.file(fileName, r.blob);
          fileCount += 1;
        }
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `glowytics_esitlenmis_${fileCount}_gorsel.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch (e) {
      setGlobalError(e?.message || 'ZIP oluşturulamadı.');
    } finally {
      setZipping(false);
    }
  }, [items]);

  const hasItems = items.length > 0;
  const resultCount = items.filter((it) => it.results?.length).length;
  const bottleHpx = Math.round(outH * ((bottomTargetPct - topTargetPct) / 100));
  const working = busy || equalizing || detecting;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-50 text-sky-700 text-xs font-semibold ring-1 ring-sky-200 mb-3">
            <Ruler className="w-3.5 h-3.5" />
            Şişe Konum Eşitleme
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Görsel Boyut Eşitleyici</h1>
          <p className="mt-2 text-gray-600 text-sm max-w-3xl leading-relaxed">
            Farklı boyutlardaki PNG'leri yükle. AI her şişenin <strong>kutusunu</strong> (üst/alt + sol/sağ)
            bulur, gerekirse <strong>dört kenarı elle sürükleyerek</strong> düzeltirsin. Çıktıda her görselde
            şişe <strong>tam aynı yerde başlar/biter</strong> (boy üst→alt sabit, yatay konum sol/sağ ortası sabit);
            dekor şişeyle birlikte kayar.
          </p>
        </div>

        {/* Upload area */}
        <div
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDragOver={onDragOver}
          onDrop={onDrop}
          className={`bg-white rounded-2xl ring-1 shadow-sm mb-5 transition-all ${isDragging ? 'ring-2 ring-sky-300 bg-sky-50' : 'ring-gray-200'}`}
        >
          {!hasItems ? (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex flex-col items-center justify-center gap-3 py-12 text-gray-400 hover:text-sky-600 transition-colors"
            >
              <Upload className="w-10 h-10" />
              <div className="text-center">
                <p className="text-sm font-medium">PNG görsellerini yükle (birden fazla seçebilirsin)</p>
                <p className="text-xs">veya buraya sürükle bırak</p>
              </div>
            </button>
          ) : (
            <div className="px-4 py-3 flex flex-wrap items-center gap-3">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={working}
                className="inline-flex items-center gap-1.5 text-xs font-semibold bg-sky-600 hover:bg-sky-700 disabled:bg-gray-300 text-white px-3 py-1.5 rounded-lg"
              >
                <Plus className="w-3.5 h-3.5" /> Görsel ekle
              </button>
              <div className="text-xs text-gray-600">
                <strong>{items.length}</strong> görsel
                {busy && <span className="ml-2 inline-flex items-center gap-1 text-sky-700"><Loader2 className="w-3 h-3 animate-spin" /> analiz ediliyor…</span>}
                {stats?.aiCount > 0 && <span className="ml-2 text-sky-700">{stats.aiCount} AI</span>}
                {stats?.manualCount > 0 && <span className="ml-2 text-amber-700">{stats.manualCount} elle</span>}
                {resultCount > 0 && <span className="ml-2 text-emerald-700">{resultCount} eşitlendi</span>}
              </div>
              <button
                onClick={clearAll}
                disabled={working}
                className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-red-600 disabled:opacity-50 px-2 py-1.5"
              >
                <Trash2 className="w-3.5 h-3.5" /> Temizle
              </button>
            </div>
          )}
          <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={onFileInputChange} className="hidden" />
        </div>

        {/* Global error */}
        {globalError && (
          <div className="mb-5 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{globalError}</span>
          </div>
        )}

        {/* Control panel */}
        {hasItems && (
          <div className="bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm p-5 mb-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {/* Output size */}
              <div>
                <label className="block text-[11px] font-medium text-gray-500 mb-1.5">Çıktı boyutu (G × Y px)</label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {OUTPUT_PRESETS.map((p) => (
                    <button key={p.label} onClick={() => { setOutW(p.w); setOutH(p.h); }}
                      className={`px-2.5 py-1.5 rounded-lg text-xs font-medium ${outW === p.w && outH === p.h ? 'bg-sky-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input type="number" min={100} max={6000} value={outW}
                    onChange={(e) => setOutW(Math.max(1, Number(e.target.value) || 0))}
                    className="w-20 px-2 py-1 text-sm border border-gray-200 rounded-lg tabular-nums" />
                  <span className="text-gray-400 text-xs">×</span>
                  <input type="number" min={100} max={6000} value={outH}
                    onChange={(e) => setOutH(Math.max(1, Number(e.target.value) || 0))}
                    className="w-20 px-2 py-1 text-sm border border-gray-200 rounded-lg tabular-nums" />
                  <span className="text-[10px] text-gray-400">{bgEnabled ? `arka plan ${bgColor}` : 'şeffaf PNG'}</span>
                </div>
                {alsoSquare && outW !== outH && (
                  <p className="mt-2 text-[11px] text-sky-600">+ kare: {outW}×{outW} (birlikte üretilir)</p>
                )}
              </div>

              {/* Top target */}
              <div>
                <label className="block text-[11px] font-medium text-gray-500 mb-1.5">
                  Şişe <span className="text-emerald-600 font-semibold">üstü</span>: %{topTargetPct} = {Math.round(outH * topTargetPct / 100)}px
                </label>
                <input type="range" min={0} max={Math.max(0, bottomTargetPct - 5)} value={topTargetPct}
                  onChange={(e) => setTopTargetPct(Number(e.target.value))} className="w-full accent-emerald-600" />
                <label className="block text-[11px] font-medium text-gray-500 mt-2 mb-1.5">
                  Şişe <span className="text-rose-600 font-semibold">altı</span>: %{bottomTargetPct} = {Math.round(outH * bottomTargetPct / 100)}px
                </label>
                <input type="range" min={Math.min(100, topTargetPct + 5)} max={100} value={bottomTargetPct}
                  onChange={(e) => setBottomTargetPct(Number(e.target.value))} className="w-full accent-rose-600" />
                <label className="block text-[11px] font-medium text-gray-500 mt-2 mb-1.5">
                  Şişe <span className="text-sky-600 font-semibold">yatay konumu</span>: %{anchorXPct} = {Math.round(outW * anchorXPct / 100)}px
                </label>
                <input type="range" min={10} max={90} value={anchorXPct}
                  onChange={(e) => setAnchorXPct(Number(e.target.value))} className="w-full accent-sky-600" />
              </div>

              {/* Auto detect + info */}
              <div className="space-y-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={autoDetect} onChange={(e) => setAutoDetect(e.target.checked)} className="mt-0.5 w-4 h-4 accent-sky-600" />
                  <span>
                    <span className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                      <Crosshair className="w-4 h-4 text-sky-600" /> Eşitlerken AI otomatik tespit
                    </span>
                    <span className="block text-[11px] text-gray-500 mt-0.5">
                      Henüz dokunulmamış görsellerde şişeyi AI bulur. Elle ayarladığın çizgilere dokunmaz.
                    </span>
                  </span>
                </label>
                {/* Arka plan rengi */}
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={bgEnabled} onChange={(e) => setBgEnabled(e.target.checked)} className="w-4 h-4 accent-sky-600" />
                    <span className="text-sm font-semibold text-gray-800">Arka plan rengi</span>
                    <input type="color" value={bgColor} disabled={!bgEnabled} onChange={(e) => setBgColor(e.target.value)}
                      className="w-7 h-7 rounded border border-gray-200 disabled:opacity-40 p-0.5 cursor-pointer" />
                    <input type="text" value={bgColor} disabled={!bgEnabled}
                      onChange={(e) => setBgColor(e.target.value)}
                      className="w-24 px-2 py-1 text-xs border border-gray-200 rounded-lg tabular-nums uppercase disabled:opacity-40" />
                  </label>
                  <span className="block text-[11px] text-gray-500 mt-0.5 ml-6">Kapalıysa çıktı şeffaf PNG olur.</span>
                </div>

                {/* Kare (1:1) versiyonu */}
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={alsoSquare} onChange={(e) => setAlsoSquare(e.target.checked)} className="mt-0.5 w-4 h-4 accent-sky-600" />
                  <span>
                    <span className="text-sm font-semibold text-gray-800">Kare (1:1) versiyonu da üret</span>
                    <span className="block text-[11px] text-gray-500 mt-0.5">
                      {outW}×{outH} yanında {outW}×{outW} kare çıktı da üretilir, birlikte export edilir.
                    </span>
                  </span>
                </label>

                <div className="text-[11px] text-gray-500 bg-gray-50 rounded-lg px-3 py-2 flex items-center gap-1.5">
                  <MoveVertical className="w-3.5 h-3.5 text-sky-500 shrink-0" />
                  Şişe yüksekliği çıktıda hep <strong className="text-gray-700">~{bottleHpx}px</strong> ({outW}×{outH})
                </div>
              </div>
            </div>

            <div className="mt-5 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-3">
              <button
                onClick={() => runDetect()}
                disabled={working}
                className="inline-flex items-center gap-2 bg-white ring-1 ring-sky-300 text-sky-700 hover:bg-sky-50 disabled:opacity-50 text-sm font-semibold px-4 py-2 rounded-lg"
              >
                {detecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4" />}
                AI ile şişeleri tespit et
              </button>
              <button
                onClick={runEqualize}
                disabled={working}
                className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-700 disabled:bg-gray-300 text-white text-sm font-semibold px-5 py-2 rounded-lg shadow-sm"
              >
                {(equalizing || detecting) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scaling className="w-4 h-4" />}
                {(equalizing || detecting)
                  ? `${progress.label}… ${progress.done}/${progress.total}`
                  : `Eşitle (${stats?.count || 0})`}
              </button>
              {resultCount > 0 && !working && (
                <button
                  onClick={downloadAllZip}
                  disabled={zipping}
                  className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white text-sm font-semibold px-4 py-2 rounded-lg"
                >
                  {zipping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                  Hepsini ZIP indir ({resultCount})
                </button>
              )}
            </div>
            <p className="mt-3 text-[11px] text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
              💡 <span className="text-emerald-600 font-semibold">ÜST</span> /
              <span className="text-rose-600 font-semibold"> ALT</span> = şişenin tepesi/tabanı;
              <span className="text-sky-600 font-semibold"> SOL</span> /
              <span className="text-violet-600 font-semibold"> SAĞ</span> = şişenin (ya da kapağın) sol/sağ kenarı.
              Yatay hiza bu iki kenarın <strong>ortasından</strong> (sarı <span className="text-amber-600 font-semibold">MERKEZ</span> çizgisi)
              alınır ve çıktıda kanvasın tam ortasına oturur. Kayma varsa <span className="text-amber-600 font-semibold">MERKEZ</span> çizgisini
              sürükle — SOL/SAĞ birlikte kayar, şişeyi elle tam ortalarsın. Düzelttiğin eksen kilitlenir, diğerini AI yine doldurur.
            </p>
          </div>
        )}

        {/* Items list */}
        {hasItems && (
          <div className="space-y-3">
            {items.map((it, idx) => (
              <div key={it.id} className="bg-white rounded-2xl ring-1 ring-gray-200 shadow-sm overflow-hidden">
                <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2">
                  <span className="text-[10px] font-semibold text-gray-400">#{idx + 1}</span>
                  <span className="text-sm font-medium text-gray-800 truncate flex-1" title={it.name}>{it.name}</span>
                  {it.aiDone && (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 inline-flex items-center gap-1">
                      <Crosshair className="w-3 h-3" /> AI
                    </span>
                  )}
                  {(it.vManual || it.hManual) && (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 inline-flex items-center gap-1">
                      <MoveVertical className="w-3 h-3" /> elle
                    </span>
                  )}
                  {it.results?.length > 0 && (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 inline-flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> {it.results.length} çıktı
                    </span>
                  )}
                  <button onClick={() => removeItem(it.id)} disabled={working} className="text-gray-400 hover:text-red-600 disabled:opacity-30" title="Kaldır">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-[300px_1fr_220px]">
                  {/* Original preview + draggable guides */}
                  <div className={`${CHECKER_BG} flex items-center justify-center p-3 border-b md:border-b-0 md:border-r border-gray-100 min-h-[220px]`}>
                    {it.previewUrl ? (
                      <GuideOverlay item={it} disabled={working} onChange={(patch) => updateItem(it.id, patch)} />
                    ) : (
                      <div className="text-xs text-red-600 text-center px-2">{it.error || 'Yüklenemedi'}</div>
                    )}
                  </div>

                  {/* Analysis */}
                  <div className="p-4 min-w-0 border-b md:border-b-0 md:border-r border-gray-100">
                    {it.box ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mb-1"><Crop className="w-3 h-3" /> Kanvas (dosya)</div>
                          <p className="text-base font-bold text-gray-900 tabular-nums">{it.canvasW} × {it.canvasH}<span className="text-xs font-medium text-gray-400 ml-1">px</span></p>
                        </div>
                        <div className="rounded-lg p-3 bg-sky-50">
                          <div className="flex items-center gap-1.5 text-[11px] mb-1 text-sky-600">
                            <Crosshair className="w-3 h-3" /> Şişe konumu
                          </div>
                          <p className="text-base font-bold text-sky-800 tabular-nums">
                            Ü%{it.topPct.toFixed(0)} → A%{it.bottomPct.toFixed(0)}
                          </p>
                          <p className="text-[10px] text-sky-500 mt-0.5 tabular-nums">
                            <span className="text-sky-600">Sol</span> %{it.leftPct.toFixed(0)} · <span className="text-violet-600">Sağ</span> %{it.rightPct.toFixed(0)} · orta %{((it.leftPct + it.rightPct) / 2).toFixed(0)}
                          </p>
                        </div>
                        <p className="sm:col-span-2 text-[11px] text-gray-500">
                          {it.detectError
                            ? <span className="text-red-600">⚠ {it.detectError} — çizgileri elle sürükle.</span>
                            : !it.aiDone && !it.vManual && !it.hManual
                              ? <>Otomatik başlangıç (içerik sınırı). AI tespiti veya elle düzeltme önerilir.</>
                              : <>
                                  {it.aiDone ? 'AI' : 'Elle'}
                                  {(it.vManual || it.hManual) ? ' + elle düzeltme' : ''} — şişenin tepesi/tabanı ve yatay merkezi doğru mu kontrol et.
                                </>}
                          {it.results?.length > 0 && <span className="text-emerald-700 font-medium"> → çıktı: {it.results.map((r) => r.label).join(', ')}.</span>}
                        </p>
                      </div>
                    ) : (
                      <div className="text-xs text-red-600">{it.error || 'Boyut hesaplanamadı.'}</div>
                    )}
                  </div>

                  {/* Result(s) */}
                  <div className={`${CHECKER_BG} flex flex-col items-center justify-center gap-2 p-3 min-h-[220px] relative`}>
                    {(equalizing || detecting) && !it.results?.length && it.box && (
                      <Loader2 className="w-6 h-6 animate-spin text-sky-500" />
                    )}
                    {it.results?.length ? (
                      it.results.map((r) => (
                        <div key={r.label} className="flex items-center gap-2 w-full">
                          <img src={r.url} alt={`eşitlenmiş ${r.label}`} className="max-w-[92px] max-h-[110px] object-contain ring-1 ring-gray-200 rounded bg-white" />
                          <div className="min-w-0">
                            <div className="text-[11px] font-semibold text-gray-700 tabular-nums">{r.outW}×{r.outH}</div>
                            <button onClick={() => downloadResult(it, r)}
                              className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 rounded-md">
                              <Download className="w-3 h-3" /> İndir
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      !(equalizing || detecting) && <p className="text-[10px] text-gray-400 text-center">Eşitlenmiş çıktı<br />burada görünecek</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
